import { MessageSquare, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useBrand } from '@/config/brands';

interface AiSessionControlsProps {
  messagesUsed: number;
  maxMessages: number;
  canFinalize: boolean;
  onFinalize: () => void;
  isFinishing?: boolean;
}

export function AiSessionControls({
  messagesUsed,
  maxMessages,
  canFinalize,
  onFinalize,
  isFinishing,
}: AiSessionControlsProps) {
  const brand = useBrand();
  const isAhp = brand.design === 'civic';

  const remaining = maxMessages - messagesUsed;
  const pct = (messagesUsed / maxMessages) * 100;

  return (
    <div className={`flex items-center gap-3 px-4 py-2 border-t ${
      isAhp ? 'border-gray-200 bg-gray-50' : 'border-white/10 bg-black/20'
    }`}>
      {/* Message counter */}
      <div className="flex items-center gap-2 flex-1">
        <MessageSquare className="w-3.5 h-3.5 text-muted-foreground" />
        <div className="flex-1 max-w-[120px]">
          <div className={`h-1.5 rounded-full overflow-hidden ${
            isAhp ? 'bg-gray-200' : 'bg-white/10'
          }`}>
            <div
              className={`h-full rounded-full transition-all ${
                pct > 80
                  ? 'bg-red-500'
                  : pct > 50
                    ? isAhp ? 'bg-amber-500' : 'bg-amber-400'
                    : isAhp ? 'bg-civic-money' : 'bg-matrix-green'
              }`}
              style={{ width: `${pct}%` }}
            />
          </div>
        </div>
        <span className={`text-[10px] ${
          remaining <= 3 ? 'text-red-500 font-bold' : 'text-muted-foreground'
        }`}>
          {remaining} message{remaining !== 1 ? 's' : ''} left
        </span>
      </div>

      {/* Finalize button */}
      <Button
        onClick={onFinalize}
        disabled={!canFinalize || isFinishing}
        size="sm"
        className={`${
          isAhp
            ? 'bg-civic-money hover:bg-[#234d3b] text-white'
            : 'bg-amber-500 hover:bg-amber-600 text-black'
        }`}
      >
        <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" />
        {isFinishing ? 'Finalizing...' : 'Finalize Scenario'}
      </Button>
    </div>
  );
}
